import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import KeepExploring from '../../components/feature/KeepExploring';
import PlaceCard from './components/PlaceCard';
import SEO from '../../components/feature/SEO';
import { places, categoryConfig, categoryOrder, ThingsToDoCategory } from '../../mocks/thingsToDo';

export default function ThingsToDoPage() {
  const [activeCategory, setActiveCategory] = useState<ThingsToDoCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  const filtered = useMemo(() => {
    return places.filter((p) => {
      const matchesCat = activeCategory === 'all' || p.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.location.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q));
      return matchesCat && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Things To Do With Kids in Gwinnett County | The Mommy Mingle',
    description:
      'Discover family-friendly activities, parks, indoor play areas, and splash pads in Gwinnett County, Georgia.',
    url: `${siteUrl}/things-to-do`,
    publisher: {
      '@type': 'Organization',
      name: 'The Mommy Mingle',
      url: siteUrl,
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Things To Do With Kids in Gwinnett County | The Mommy Mingle"
        description="Discover family-friendly parks, indoor play areas, splash pads, and libraries in Gwinnett County, Georgia. Trusted by local moms across Gwinnett and surrounding counties."
        keywords="things to do with kids Gwinnett County, family activities Georgia, indoor play Gwinnett, parks Sugar Hill GA"
        canonical="/things-to-do"
        schema={schema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/34bb91e07b77b377bb3d704459cd2387.jpg"
            alt="Things to do in Gwinnett"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65"></div>
        </div>

        <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
            <i className="ri-map-pin-2-fill text-rose-300"></i>
            Gwinnett County &amp; Surrounding Areas
          </div>

          <h1
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Things To <span className="text-rose-300">Do</span>
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Parks, indoor play, splash pads, libraries, and family-friendly spots — places you can visit any time, no tickets or schedules required.
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
              <input
                type="text"
                placeholder="Search parks, play areas, libraries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-full bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
              />
            </div>
          </div>

          {/* Category jumps */}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === 'all'
                  ? 'bg-white text-gray-900'
                  : 'bg-white/20 text-white border border-white/30 hover:bg-white/30'
              }`}
            >
              All Places
            </button>
            {categoryOrder.map((cat) => {
              const cfg = categoryConfig[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? 'bg-white text-gray-900'
                      : 'bg-white/20 text-white border border-white/30 hover:bg-white/30'
                  }`}
                >
                  <i className={`${cfg.icon} text-xs`}></i>
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Intro note */}
      <section className="bg-rose-50 border-b border-rose-100 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-rose-700 font-medium text-center sm:text-left">
              <i className="ri-information-line mr-1.5"></i>
              These are <strong>evergreen places</strong> — open year-round for drop-in visits. Looking for upcoming events?{' '}
              <Link to="/events" className="underline font-bold hover:text-rose-900 cursor-pointer">
                See what&apos;s happening this week →
              </Link>
            </p>
            <div className="flex items-center gap-1.5 text-xs text-rose-500 shrink-0">
              <i className="ri-shield-check-line"></i>
              Trusted by local moms across Gwinnett
            </div>
          </div>
        </div>
      </section>

      {/* Search result notice */}
      {searchQuery && (
        <div className="bg-gray-50 border-b border-gray-100 py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600 font-medium">
              {filtered.length} place{filtered.length !== 1 ? 's' : ''} found for &ldquo;<strong>{searchQuery}</strong>&rdquo;
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-xs text-gray-400 hover:text-gray-700 font-semibold cursor-pointer whitespace-nowrap"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Places Grid */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeCategory === 'all' && !searchQuery ? (
            // Group by category
            categoryOrder.map((cat) => {
              const group = places.filter((p) => p.category === cat);
              if (group.length === 0) return null;
              const cfg = categoryConfig[cat];
              return (
                <div key={cat} className="mb-16">
                  {/* Category header */}
                  <div className="flex items-center gap-3 mb-8">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${cfg.accent}`}>
                      <i className={`${cfg.icon} ${cfg.color} text-lg`}></i>
                    </div>
                    <div>
                      <h2
                        className="text-xl font-bold text-gray-900"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {cfg.label}
                      </h2>
                      <p className="text-xs text-gray-400">{group.length} places</p>
                    </div>
                    <button
                      onClick={() => setActiveCategory(cat)}
                      className="ml-auto text-xs font-semibold text-rose-500 hover:text-rose-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      View all →
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {group.slice(0, 4).map((place) => (
                      <PlaceCard key={place.id} place={place} />
                    ))}
                  </div>
                </div>
              );
            })
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">No places found</h3>
              <p className="text-sm text-gray-400">Try a different search or category.</p>
            </div>
          ) : (
            <>
              {/* Filtered header */}
              {activeCategory !== 'all' && (
                <div className="flex items-center gap-3 mb-8">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${categoryConfig[activeCategory].accent}`}>
                    <i className={`${categoryConfig[activeCategory].icon} ${categoryConfig[activeCategory].color} text-lg`}></i>
                  </div>
                  <div>
                    <h2
                      className="text-xl font-bold text-gray-900"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {categoryConfig[activeCategory].label}
                    </h2>
                    <p className="text-xs text-gray-400">{filtered.length} places</p>
                  </div>
                  <button
                    onClick={() => setActiveCategory('all')}
                    className="ml-auto text-xs font-semibold text-gray-400 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    ← All categories
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((place) => (
                  <PlaceCard key={place.id} place={place} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 bg-gradient-to-br from-rose-50 to-amber-50 border-t border-rose-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl font-bold text-gray-900 mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Know a great spot we missed?
          </h2>
          <p className="text-sm text-gray-500 max-w-xl mx-auto mb-6">
            Join The Mommy Mingle and share your favorite local spots with thousands of Gwinnett moms.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="https://www.facebook.com/groups/themommymingle"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-rose-500 text-white text-sm font-bold rounded-full hover:bg-rose-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-group-line"></i>
              Join The Mommy Mingle
            </a>
            <Link
              to="/events"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-800 text-gray-800 text-sm font-bold rounded-full hover:bg-gray-800 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-event-line"></i>
              See What&apos;s Happening This Week
            </Link>
          </div>
        </div>
      </section>

      <KeepExploring
        title="Keep Exploring"
        subtitle="Trusted by local moms across Gwinnett and surrounding areas."
        items={[
          { label: 'Explore Events', icon: 'ri-calendar-event-line', to: '/events' },
          { label: 'Seasonal Guides', icon: 'ri-sun-line', href: 'https://gwinnettcampguide.com' },
          { label: 'Join The Mommy Mingle', icon: 'ri-group-line', href: 'https://www.facebook.com/groups/themommymingle', primary: true },
        ]}
        bg="white"
      />

      <Footer />
    </div>
  );
}
